import { Injectable, OnDestroy } from '@angular/core';
import { OPTIONS } from '@shared/const/globals.const';
import { TaskDal } from '@shared/dal/task.dal';
import { Option } from '@shared/models/option.model';
import { OPTIONS_TO_TASK_OPERATION, Task, TaskOperationsEnum, TasksPaginatedResponse } from '@shared/models/task.model';
import { Observable, Subject } from 'rxjs';
import { SocketService } from 'src/app/core/services/socket.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class TaskService implements OnDestroy {
  private socket: any | null = null;

  private taskUpdateSubject = new Subject<Task>();

  public taskUpdate$ = this.taskUpdateSubject.asObservable();

  private hospitalOptions: Option[] | undefined;

  private reloadTasksSubject = new Subject<void>();

  public reloadTasks$ = this.reloadTasksSubject.asObservable();

  constructor(
    private taskDal: TaskDal,
    private socketService: SocketService,
    private userService: UserService
  ) {
    this.getHospitalOptions();
  }

  getHospitalOptions() {
    const currentUserHospitals = this.userService.getCurrentUserHospitals();
    const hospitalId = this.userService.getSelectedHospitals()?.[0];
    const currentHospital = currentUserHospitals?.find((hospital: any) => hospital._id === hospitalId);
    this.hospitalOptions = currentHospital?.options;
  }

  getTasks(status: string, page: number, limit: number): Observable<TasksPaginatedResponse> {
    return this.taskDal.getAllTasks(status, page, limit);
  }

  getRunningTasks(page: number = 1, limit: number = 10): Observable<TasksPaginatedResponse> {
    return this.getTasks('running', page, limit);
  }

  getAllTasks(page: number = 1, limit: number = 10): Observable<TasksPaginatedResponse> {
    return this.getTasks('ALL', page, limit);
  }

  retryTask(taskId: string): Observable<void> {
    return this.taskDal.retryTask(taskId);
  }

  cancelTask(taskId: string): Observable<Task> {
    return this.taskDal.cancelTask(taskId);
  }

  triggerReload(): void {
    this.getHospitalOptions();

    this.reloadTasksSubject.next();
  }

  connect(): void {
    this.socket = this.socketService.getSocket();

    if (!this.socket) {
      return;
    }

    this.socket.on('tasks:update', (taskData: { task: Task }): void => {
      if (!this.isHiddenTask(taskData.task.operation)) {
        this.taskUpdateSubject.next(taskData.task);
      }
    });
  }

  isHiddenTask(operation: TaskOperationsEnum): boolean {
    if (!this.hospitalOptions) {
      return false;
    }

    const connectorTask = Object.values(OPTIONS_TO_TASK_OPERATION)
      .flatMap((x) => x)
      .includes(operation);

    if (!connectorTask) {
      return false;
    }

    const option = this.hospitalOptions.find((option: Option) =>
      OPTIONS_TO_TASK_OPERATION[option.name as keyof typeof OPTIONS]?.includes(operation)
    );

    return !option?.showTasksInFront;
  }

  disconnectSocket(): void {
    if (this.socket) {
      this.socket.off('tasks:update');
    }
  }

  ngOnDestroy(): void {
    this.disconnectSocket();
    this.taskUpdateSubject.complete();
    this.reloadTasksSubject.complete();
  }
}
